import { apiRequest } from 'utils/request'

/**
 * Get Group by search.
 * Full params example: {{baseUrl}}/v2.0/groups/search?type=rp&name=test&description=test&vehicleSubType=M2&status=Active
 * @typedef {object} GetGroupsType
 * @property {'RP'|'PTO'|'FEATURE'} groupType
 * @property {'M2'|'B2'|'RV'} vehicleSubType
 * @property {'Design'|'Active'|'Obsolete'} [status] - Optional status filter (PBI 150666)
 *
 * @param {GetGroupsType} param0
 * @returns {Promise<any>}
 */
export const getGroups = ({ groupType, status }) => {
  let uri = `/groups/search?type=${groupType}`
  if (status) {
    uri += `&status=${status}`
  }
  return apiRequest({
    method: 'GET',
    uri,
  })
}

/**
 * Get Group.
 * @param {string} param0
 * @returns {Promise<any>}
 */
export const getGroup = ({ id }) =>
  apiRequest({
    method: 'GET',
    uri: `/groups/${id}`,
  })

/**
 * Add Collection
 * @typedef {object} AddCollectionType
 * @property {string} title
 * @property {string} description
 * @property {array} partUris
 * @property {'RP'|'PTO'|'FEATURE'} collectionType
 * @property {string|null} buildDate - ISO 8601 datetime string. For vehicle-based
 *   collections this is the vehicle's actual build date. For manually created
 *   collections this is January 1 noon UTC of the selected model year
 *   (e.g. "2025-01-01T12:00:00Z"). Used for VPDM validation (is43NAlignedWith42N).
 *
 * @param {AddCollectionType} param0
 * @returns {Promise<any>}
 */
export const addCollection = ({
  title,
  description,
  vehicleSubType,
  partUris,
  collectionType,
  varMinCompat,
  mdlDbCd,
  buildDate,
}) =>
  apiRequest({
    method: 'POST',
    uri: `groups/create`,
    body: {
      name: title,
      description,
      vehicleSubType,
      type: collectionType,
      partUris,
      varMinCompat,
      mdlDbCd,
      buildDate,
    },
  })

/**
 * Update Collection - Add/Remove part URIs and optionally update name/description
 * POST /groups/{id}/update
 * @param {object} param0
 * @param {string|number} param0.id - Collection ID
 * @param {string[]} param0.addPartUris - URIs to add (e.g. ["/features/123"])
 * @param {string[]} param0.removePartUris - URIs to remove
 * @param {string} [param0.name] - Updated collection name
 * @param {string} [param0.description] - Updated collection description
 * @returns {Promise<any>}
 */
export const updateCollection = ({
  id,
  addPartUris,
  removePartUris,
  name,
  description,
}) => {
  const body = {
    AddPartUris: addPartUris,
    RemovePartUris: removePartUris,
  }
  if (name !== undefined) body.name = name
  if (description !== undefined) body.description = description
  return apiRequest({
    method: 'POST',
    uri: `groups/${id}/update`,
    body,
  })
}

/**
 * Update Collection Metadata - name, description, varMinCompat, mdlDbCd
 * POST /groups/{id}/update-metadata (future API)
 * @param {object} param0
 * @param {string|number} param0.id - Collection ID
 * @param {string} param0.name - Collection name
 * @param {string} param0.description - Collection description
 * @param {string} param0.varMinCompat - Minimum compatible variant
 * @param {string} param0.mdlDbCd - Model data-book code
 * @returns {Promise<any>}
 */
export const updateCollectionMetadata = ({
  id,
  name,
  description,
  varMinCompat,
  mdlDbCd,
}) =>
  apiRequest({
    method: 'POST',
    uri: `groups/${id}/update-metadata`,
    body: {
      name,
      description,
      varMinCompat,
      mdlDbCd,
    },
  })

/**
 * Activate Collection (PBI 150666)
 * Changes collection status from 'Design' to 'Active'
 * @param {object} param0
 * @param {string} param0.id - Collection ID
 * @returns {Promise<any>}
 */
export const activateCollection = ({ id }) =>
  apiRequest({
    method: 'POST',
    uri: `/groups/${id}/status`,
    body: { status: 'Active' },
  })

/**
 * Obsolete Collection (PBI 150666)
 * Changes collection status from 'Active' to 'Obsolete'
 * @param {object} param0
 * @param {string} param0.id - Collection ID
 * @returns {Promise<any>}
 */
export const obsoleteCollection = ({ id }) =>
  apiRequest({
    method: 'POST',
    uri: `/groups/${id}/status`,
    body: { status: 'Obsolete' },
  })

/**
 * Duplicate Collection (PBI 150666)
 * Creates a copy of an Active or Obsolete collection in 'Design' status
 * @param {object} param0
 * @param {string} param0.id - Collection ID to duplicate
 * @returns {Promise<any>}
 */
export const duplicateCollection = ({ id, partUris }) =>
  apiRequest({
    method: 'POST',
    uri: `/groups/${id}/duplicate`,
    ...(partUris ? { body: { partUris } } : {}),
  })

/**
 * Check feature compatibility against a Feature Collection.
 *
 * POST /groups/{id}/features/compatible
 *
 * The backend evaluates each feature URI against the collection's metadata
 * (varMinCompat, vehicle sub-type, QuickFit Sales Code, build date context, etc.)
 * and returns an isCompatible flag and an optional incompatibilityReason for each.
 *
 * This is a standalone validation call (not tied to search). The vehicle flow
 * achieves the same result by passing `serial` + `filterConflicts: true` to the
 * search API so the backend decorates each result inline. Collections have no
 * serial number, so this dedicated endpoint is called after features are selected
 * and before they are added to the working set.
 *
 * @param {object} param0
 * @param {string|number} param0.id - Collection ID
 * @param {string[]} param0.featureUris - Array of feature URIs to check, e.g. ["/features/123"]
 * @returns {Promise<{ features: Array<{ uri: string, isCompatible: boolean, incompatibilityReason?: string }> }>}
 */
export const getCollectionFeatureCompatibility = ({ id, featureUris }) =>
  apiRequest({
    method: 'POST',
    readOnly: true,
    uri: `/groups/${id}/features/compatible`,
    body: { featureUris },
  })

/**
 * Validate pin conflicts for a proposed feature change set against a Feature Collection.
 *
 * POST /groups/{id}/features/validate
 *
 * Collection equivalent of POST /vehicles/{serialNumber}/features/validate.
 * Returns module input, output, and CAN output conflicts if any features in
 * addedFeatures conflict with existing collection features on the same pin.
 *
 * @param {object} param0
 * @param {string|number} param0.id - Collection ID
 * @param {string[]} param0.addedFeatures - URIs of features being added
 * @param {string[]} param0.removedFeatures - URIs of features being removed
 * @returns {Promise<{ moduleInputConflicts: object[], moduleOutputConflicts: object[], moduleCanOutputConflicts: object[] }>}
 */
export const requestCollectionFeaturesValidation = ({
  id,
  addedFeatures,
  removedFeatures,
}) =>
  apiRequest({
    method: 'POST',
    // readOnly: true — this is a validation/read operation; failures should be
    // silent (dev log only), not show the blocking mutation error modal that
    // says "your changes may not have been saved" (no data was mutated).
    readOnly: true,
    uri: `/groups/${id}/features/validate`,
    body: { addedFeatures, removedFeatures },
  })

/**
 * Validate dynamic content compatibility for a proposed feature change set.
 *
 * POST /groups/{id}/features/validate/dynamic-content
 *
 * Collection equivalent of POST /vehicles/{serialNumber}/features/validate/dynamic-content.
 * Called just before committing changes. If the response contains data (validation
 * issues such as exceeding max logic sets), a modal is shown and the commit is blocked
 * until the user acknowledges and resolves the issues.
 *
 * @param {object} param0
 * @param {string|number} param0.id - Collection ID
 * @param {string[]} param0.addedFeatures - URIs of features being added
 * @param {string[]} param0.removedFeatures - URIs of features being removed
 * @returns {Promise<Array>} Empty array means no issues; non-empty triggers the validation modal.
 */
export const validateCollectionDynamicContent = ({
  id,
  addedFeatures,
  removedFeatures,
}) =>
  apiRequest({
    method: 'POST',
    // readOnly: true — validation only; failures should not show the blocking
    // mutation error modal (no data is mutated by this endpoint).
    readOnly: true,
    uri: `/groups/${id}/features/validate/dynamic-content`,
    body: { addedFeatures, removedFeatures },
  })

/**
 * Get candidate logic-set capacity counts for a collection edit state.
 * This advisory preflight includes in-limit modules, unlike the overage-only
 * dynamic-content validation endpoint used at commit time.
 */
export const preflightCollectionCapacity = async ({
  id,
  addedFeatures,
  removedFeatures,
}) => {
  const response = await apiRequest({
    method: 'POST',
    readOnly: true,
    uri: `/groups/${id}/features/preflight-capacity`,
    body: { addedFeatures, removedFeatures },
  })
  return response?.data?.modules
}

/**
 * Fetch features by their IDs for a specific collection context.
 * Bypasses the standard search reducer normalization so that revision-pinned
 * features are returned as-is (without revision promotion to "latest").
 *
 * @param {{ ids: string[], vehicleSubType?: string, mdlDbCd?: string, buildDate?: string }} params
 */
export const getCollectionFeaturesByIds = ({
  ids,
  vehicleSubType,
  mdlDbCd,
  buildDate,
}) =>
  apiRequest({
    // POST with a JSON body prevents 414 URI Too Long errors for large
    // collections with many feature IDs. GET with repeated ids=... query params
    // exceeds browser/proxy URL limits (~2–8 KB) for 50+ features.
    // readOnly: true marks this as a semantic read so defaultErrorHandler treats
    // failures as silent (same pattern as getSearchResults, getCollectionFeatureCompatibility).
    method: 'POST',
    uri: `/features/search`,
    readOnly: true,
    body: {
      ids,
      ...(vehicleSubType && { vehicleSubType }),
      ...(mdlDbCd && { mdlDbCd }),
      ...(buildDate && { buildDate }),
    },
  })
